<!-- Base.Select.vue (client) -->
<template tag="클라이언트 선택 컴포넌트">
  <component
    ref="selectRef"
    :is="isAuto ? 'v-autocomplete' : 'v-select'"
    v-model="selectedValue"
    :items="options ?? []"
    item-title="label"
    item-value="value"
    density="compact"
    variant="outlined"
    :class="['form_style select_box', size ? size : '', !border ? 'border_none' : '']"
    :menu-props="menuProps"
    :placeholder="placeholder || $text('common.all')"
    :disabled="disabled"
    no-data-text="No Result"
    :error-messages="errorMessages"
    @update:model-value="
      (value: string | number | null) => emit('update:modelValue', value as string | number | null)
    "
    @update:menu="onMenuUpdate"
    autocomplete="off"
  />
</template>

<script setup lang="ts">
import { type PropType, onMounted, ref, computed, nextTick, onBeforeUnmount } from "vue";

/* ------------------------------------------------------------
                          Vue Instance & Global Properties
   ------------------------------------------------------------ */
import { i18n } from "@/app/plugins/global";
const { $text } = i18n();

/* ------------------------------------------------------------
                          Model
   ------------------------------------------------------------ */
const selectedValue = defineModel<string | number | null>();

const emit = defineEmits<{
  (e: "update:modelValue", value: string | number | null): void;
}>();

/* ------------------------------------------------------------
                          Props
   ------------------------------------------------------------ */
const props = defineProps({
  placeholder: { type: String, default: "" },
  options: {
    type: Array as PropType<{ label: string; value: string }[]>,
    default: () => [],
  },
  size: { type: String, default: "" },
  border: { type: Boolean, default: true },
  disabled: { type: Boolean, default: false },
  errorMessages: { type: Array as PropType<string[]>, default: () => [] },
  isAuto: { type: Boolean, default: false },
});

/* ------------------------------------------------------------
                          Methods
   ------------------------------------------------------------ */
/** 기본값 설정 */
function getDefaultValue() {
  return props.placeholder ? null : (props.options[0]?.value ?? null);
}

/* ------------------------------------------------------------
                          Overlay width control
   ------------------------------------------------------------ */
const selectRef = ref<any>(null);
const overlayW = ref<number | null>(null);

function measureSelectWidth() {
  const root = selectRef.value?.$el as HTMLElement | undefined;
  if (!root) return;

  const field = root.querySelector(".v-field") as HTMLElement | null;
  const w = Math.round((field ?? root).getBoundingClientRect().width);

  if (w > 0) overlayW.value = w;
}

const menuProps = computed(() => {
  const w = overlayW.value;

  return {
    contentClass: `combobox_overlay select_overlay ${props.size ? props.size : ""}`,
    ...(w ? { width: w, minWidth: w, maxWidth: w } : {}),
  };
});

const onMenuUpdate = async (open: boolean) => {
  if (!open) return;
  await nextTick();
  measureSelectWidth();
};

const onResize = () => measureSelectWidth();

onMounted(async () => {
  if (selectedValue.value === null || selectedValue.value === undefined) {
    selectedValue.value = getDefaultValue();
  }
  await nextTick();
  measureSelectWidth();
  window.addEventListener("resize", onResize);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", onResize);
});
</script>
